// Component for drag and drop or file picker image uploading - 
    // returns imageDetails which contains a url and name
    // returns uploadedImage which contains the base64 image data ready for an api call

// Import react elements
import { useState, useRef } from "react";

// Import utility functions
import { toBase64 } from "global/utilities/ConvertImage";



const useImageUploader = (selectedImage, addImages, addToImageBucket = true) => {


    // Set state for image details
    const [imageDetails, setImageDetails] = useState(() => selectedImage || [] );

    // Set loading state for file upload
    const [isLoadingWhileAddingImages, setIsLoadingWhileAddingImages] = useState(false);

    // Set loading state for adding POD design
    //const [isLoadingAddingPodDesign, setIsLoadingAddingPodDesign] = useState(false);

    // Set chosen image
    const [uploadedImage, setUploadedImage] = useState(false);

    // Ref to track drag and drop input
    const imageInputRef = useRef();



    // Handle file/s drop
    const _handleDragAndDrop = async (event) => {

        setIsLoadingWhileAddingImages(true);

        // Prevent default behaviour
        event.preventDefault();

        // Extract the first file from the drag-and-drop event
        const file = event.dataTransfer.files?.[0];

        // Check that the file type starts with "image/"
        if (file?.type.startsWith("image/")) {

            // Convert file to base64 image
            const base64Image = await toBase64(file);

            // Set setUploadedImage to the base64Image
            setUploadedImage(base64Image);

            // Create a temporary URL for the image preview
            const imageUrl = URL.createObjectURL(file);

            // Create array of new image data 
            const imageDetails = {
                image_url: imageUrl,
                name: file.name.replace(/\.[^/.]+$/, ''),
            };

            // Set the imageDetails to the new array
            setImageDetails(imageDetails);

        }

        setIsLoadingWhileAddingImages(false);

    };



    // Trigger the file input manually (useful when users click a custom "Browse" button instead of the native input)
    const openFilePicker = () => {

        // The input is hidden so simulate a click event on it
        imageInputRef.current.click();

    };



    // Handle file/s picker
    const _handleFileChange = async (e) => {

        setIsLoadingWhileAddingImages(true);

        // Extract the file from the file picker event
        const file = e.target.files[0];

        // Check that the file type starts with "image/"
        if (file?.type.startsWith("image/")) {

            // Convert file to base64 image
            const base64Image = await toBase64(file);

            // Set setUploadedImage to the base64Image
            setUploadedImage(base64Image);

            // Create a temporary URL for the image preview
            const imageUrl = URL.createObjectURL(file);

            // Create array of new image data 
            const imageDetails = {
                image_url: imageUrl,
                name: file.name.replace(/\.[^/.]+$/, ''),
            };

            // Set the imageDetails to the new array
            setImageDetails(imageDetails);

        };

        setIsLoadingWhileAddingImages(false);

    };



    // Upload a file to the Image Bucket and select the returned image
    const _ProcessImageFile = async (file) => {

        // Stop if no file was provided
        if (!file) return;

        // Stop if the selected file is not an image
        if (!file.type?.startsWith("image/")) return;

        // Stop if the Image Bucket upload function is unavailable
        if (!addImages) return;

        // Show the loading state
        setIsLoadingWhileAddingImages(true);

        try {

            // Remove the file extension from the image name
            const imageName = file.name.replace(/\.[^/.]+$/, "");

            // Upload the image through the Image Bucket
            const uploadedBucketImages = await addImages([

                // Provide the data expected by addImages
                {

                    // Set the cleaned image name
                    name: imageName,

                    // Set the original image file
                    file: file,

                },

            ]);

            // Get the newly uploaded Image Bucket record
            const uploadedBucketImage = uploadedBucketImages?.[0];

            // Stop if the upload did not return an image
            if (!uploadedBucketImage?.id) return;

            // Use the thumbnail where available
            const selectedImageUrl =
                uploadedBucketImage.thumbnail_url ||
                uploadedBucketImage.image_url;

            // Store the Image Bucket image details
            setImageDetails({

                // Set the image preview URL
                image_url: selectedImageUrl,

                // Store the Image Bucket ID as the name
                name: uploadedBucketImage.id,

                // Store the Image Bucket ID
                imageBucketId: uploadedBucketImage.id,

                // Store the full Image Bucket URL
                imageBucketUrl: uploadedBucketImage.image_url,

                // Store the Image Bucket thumbnail URL
                imageBucketThumbnail: uploadedBucketImage.thumbnail_url,

            });

            // The image has already been uploaded to the bucket
            setUploadedImage(false);

        } catch (error) {

            // Log unexpected upload errors
            console.error("Unable to upload image", error);

        } finally {

            // Hide the loading state
            setIsLoadingWhileAddingImages(false);

        }

    };



    // Process an image file as either an Image Bucket upload or local base64 upload
    const ProcessImageFile = async (file) => {

        // Stop if no file was provided
        if (!file) return;

        // Stop if the selected file is not an image
        if (!file.type?.startsWith("image/")) return;

        // Show the loading state
        setIsLoadingWhileAddingImages(true);

        try {

            // Remove the extension from the image filename
            const imageName = file.name.replace(/\.[^/.]+$/, "");

            // Convert the original file to base64
            const base64Image = await toBase64(file);

            // Check whether this upload should be added to the Image Bucket
            if (addToImageBucket) {

                // Stop if the Image Bucket upload function is unavailable
                if (typeof addImages !== "function") {

                    // Throw a helpful error
                    throw new Error(
                        "addImages must be provided when addToImageBucket is true."
                    );

                }

                // Upload the image through the Image Bucket
                const uploadedBucketImages = await addImages([

                    // Provide the format expected by addImages
                    {

                        // Set the cleaned image name
                        name: imageName,

                        // Set the original image file
                        file: file,

                    },

                ]);

                // Get the newly created Image Bucket record
                const uploadedBucketImage = uploadedBucketImages?.[0];

                // Stop if the upload did not return a complete image
                if (!uploadedBucketImage?.id) {

                    // Throw a helpful error
                    throw new Error(
                        "The Image Bucket upload did not return an image record."
                    );

                }

                // Use the thumbnail where one is available
                const selectedImageUrl =
                    uploadedBucketImage.thumbnail_url ||
                    uploadedBucketImage.image_url;

                // Store the normalized Image Bucket image details
                setImageDetails({

                    // Store the preview URL
                    image_url: selectedImageUrl,

                    // Store the bucket ID as the existing image name value
                    name: uploadedBucketImage.id,

                    // Store the original filename separately
                    fileName: imageName,

                    // Store the Image Bucket ID
                    imageBucketId: uploadedBucketImage.id,

                    // Store the full Image Bucket URL
                    imageBucketUrl: uploadedBucketImage.image_url,

                    // Store the Image Bucket thumbnail URL
                    imageBucketThumbnail: uploadedBucketImage.thumbnail_url,

                    // No base64 data is required once stored in the bucket
                    imageBase64: null,

                });

                // The image is already saved in the Image Bucket
                setUploadedImage(false);

                // Stop here so the local-file branch does not run
                return;

            }

            // Create a temporary browser URL for local preview
            const temporaryImageUrl = URL.createObjectURL(file);

            // Store the normalized local image details
            setImageDetails({

                // Store the temporary preview URL
                image_url: temporaryImageUrl,

                // Store the cleaned filename
                name: imageName,

                // Store the original filename separately
                fileName: imageName,

                // There is no Image Bucket ID
                imageBucketId: null,

                // There is no Image Bucket URL
                imageBucketUrl: null,

                // There is no Image Bucket thumbnail
                imageBucketThumbnail: null,

                // Store the base64 data
                imageBase64: base64Image,

            });

            // Store the base64 data using the existing uploadedImage state
            setUploadedImage(base64Image);

        } catch (error) {

            // Log unexpected upload errors
            console.error("Unable to process image", error);

        } finally {

            // Hide the loading state
            setIsLoadingWhileAddingImages(false);

        }

    };



    // Handle file picker selection
    const handleFileChange = async (event) => {

        // Get the selected file
        const file = event.target.files?.[0];

        // Upload and select the file
        await ProcessImageFile(file);

        // Clear the input so the same file can be selected again
        event.target.value = "";

    };



    // Handle a dragged image
    const handleDragAndDrop = async (event) => {

        // Prevent the browser from opening the dropped file
        event.preventDefault();

        // Prevent parent drop handlers from receiving the event
        event.stopPropagation();

        // Get the first dropped file
        const file = event.dataTransfer?.files?.[0];

        // Upload and select the dropped file
        await ProcessImageFile(file);

    };

   

    return {
        imageDetails,
        setImageDetails,
        uploadedImage,
        handleDragAndDrop,
        openFilePicker,
        handleFileChange,
        imageInputRef,
        isLoadingWhileAddingImages
    }

};
    
export default useImageUploader;

    